import { useEffect, useState, useSyncExternalStore } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Modal,
  Select,
  Text,
} from "@shopify/polaris";
import {
  resourceEventRequest,
  createResourceEventCommands,
} from "../../../../packages/shared/resource-events/client";
import {
  queueAccountKey,
  type QueueAccount,
} from "../../../../packages/shared/shopify-queue/client";
type Event = {
  id: string;
  connection_id: string;
  domain: string;
  kind: "product" | "customer";
  topic: string;
  resource_gid: string | null;
  state: string;
  revision: number;
  attempts: number;
  error: string | null;
  outcome: string | null;
  items_imported: number;
  pages_imported: number;
  generation: number;
  received_at: string;
  updated_at: string;
  next_attempt_at: string;
};
type Data = {
  connection: { id: string; domain: string; connected: number } | null;
  events: Event[];
  counts: { state: string; count: number }[];
  nextCursor: number | null;
};
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
const commands = new Map<
  string,
  ReturnType<typeof createResourceEventCommands>
>();
const storage = {
  get length() {
    return window.localStorage.length;
  },
  key: (i: number) => window.localStorage.key(i),
  getItem: (k: string) => window.localStorage.getItem(k),
  setItem: (k: string, v: string) => window.localStorage.setItem(k, v),
  removeItem: (k: string) => window.localStorage.removeItem(k),
};
const labels: Record<string, string> = {
  pending: "Waiting",
  running: "Processing",
  failed: "Needs review",
  completed: "Completed",
  privacy_removed: "Personal details removed",
  product_updated:
    "Product details updated; warehouse stock and history retained.",
  product_missing:
    "Product is no longer available in Shopify. Local products, stock and history are retained.",
  customer_updated: "Current customer details saved.",
  customer_deleted:
    "Deleted Shopify customer removed from the customer lookup. Warehouse order history is retained.",
  source_stale: "An older source version was skipped.",
  source_restarted:
    "The product changed during processing. Its variants will be read again.",
};
export function ResourceEventsPanel({
  account,
  canProducts,
  canCustomers,
}: {
  account: QueueAccount;
  canProducts: boolean;
  canCustomers: boolean;
}) {
  const key = queueAccountKey(account);
  if (!commands.has(key))
    commands.set(key, createResourceEventCommands(base, account, storage));
  const command = commands.get(key)!,
    state = useSyncExternalStore(command.subscribe, command.snapshot);
  const [data, setData] = useState<Data | null>(null),
    [error, setError] = useState(""),
    [filter, setFilter] = useState(""),
    [kind, setKind] = useState(""),
    [pages, setPages] = useState<number[]>([]),
    [refresh, setRefresh] = useState(0),
    [review, setReview] = useState<Event | null>(null);
  const cursor = pages[pages.length - 1],
    reload = () => {
      setPages([]);
      setRefresh((v) => v + 1);
    };
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    const query = new URLSearchParams();
    if (cursor) query.set("cursor", String(cursor));
    if (filter) query.set("state", filter);
    if (kind) query.set("kind", kind);
    void resourceEventRequest<Data>(base, account, "?" + query, {
      signal: abort.signal,
    })
      .then((v) => {
        if (!abort.signal.aborted) setData(v);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, cursor, filter, kind, refresh]);
  const allowed = (value: "product" | "customer") =>
    value === "product" ? canProducts : canCustomers;
  const locked = state.busy || !!state.pending || !data?.connection?.connected;
  const send = async () => {
    if (!review) return;
    const event = review;
    setReview(null);
    await command.submit({
      action: "retry",
      kind: event.kind,
      event_id: event.id,
      connection_id: event.connection_id,
      domain: event.domain,
      revision: event.revision,
    });
    if (command.snapshot().success) reload();
  };
  return (
    <BlockStack gap="400">
      <InlineStack gap="200">
        <Button onClick={reload}>Refresh source updates</Button>
        <Button url="/products">View products</Button>
      </InlineStack>
      <Text as="p">
        Product and customer updates from Shopify continue in the background.
        Product variants are read in complete pages. Existing warehouse stock,
        costs and work settings stay unchanged.
      </Text>
      <Text as="p">
        Deleted Shopify products remain in the warehouse with their history.
        Deleted Shopify customers leave the customer lookup; order history stays
        saved. Personal-data removal uses the separate Shopify privacy review.
      </Text>
      <Text as="p">
        Shopify inventory notices do not change physical stock in Whenly.
      </Text>
      {error && <Banner tone="critical">{error}</Banner>}
      {state.error && <Banner tone="critical">{state.error}</Banner>}
      {state.success && <Banner tone="success">{state.success}</Banner>}
      {state.pending && (
        <Banner tone="warning">
          <BlockStack gap="200">
            <Text as="p">
              A saved retry needs a confirmed reply. It remains saved after
              closing the browser.
            </Text>
            <Button
              loading={state.busy}
              disabled={!canProducts && !canCustomers}
              onClick={() =>
                void command.retry().then(() => {
                  if (command.snapshot().success) reload();
                })
              }
            >
              Retry saved action
            </Button>
          </BlockStack>
        </Banner>
      )}
      <InlineStack gap="300">
        <Select
          label="Update type"
          value={kind}
          options={[
            { label: "Products and customers", value: "" },
            { label: "Products", value: "product" },
            { label: "Customers", value: "customer" },
          ]}
          onChange={(v) => {
            setKind(v);
            setPages([]);
          }}
        />
        <Select
          label="Update state"
          value={filter}
          options={[
            { label: "All updates", value: "" },
            ...[
              "pending",
              "running",
              "failed",
              "completed",
              "privacy_removed",
            ].map((value) => ({ value, label: labels[value] })),
          ]}
          onChange={(v) => {
            setFilter(v);
            setPages([]);
          }}
        />
      </InlineStack>
      {!data && !error && <Text as="p">Loading source updates…</Text>}
      {data && (
        <>
          {!data.connection?.connected && (
            <Banner tone="warning">
              Reconnect Shopify before retrying an update.
            </Banner>
          )}
          <Text as="p">
            {data.counts.length
              ? data.counts
                  .map((c) => `${labels[c.state]}: ${c.count}`)
                  .join(" · ")
              : "No saved updates for this view."}
          </Text>
          {data.events.map((event) => (
            <Card key={event.id}>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">
                  {event.kind === "product" ? "Product" : "Customer"}{" "}
                  {event.resource_gid?.split("/").pop() || "details removed"} ·{" "}
                  {labels[event.state]}
                </Text>
                <Text as="p">
                  {event.domain} · {event.topic} · Received {event.received_at}{" "}
                  UTC
                </Text>
                <Text as="p">
                  {event.items_imported} records saved in {event.pages_imported}{" "}
                  pages. Attempts: {event.attempts}. Last change:{" "}
                  {event.updated_at} UTC.
                </Text>
                {event.state === "pending" && (
                  <Text as="p">Next check: {event.next_attempt_at} UTC.</Text>
                )}
                {event.outcome && (
                  <Text as="p">{labels[event.outcome] || event.outcome}</Text>
                )}
                {event.error && <Banner tone="warning">{event.error}</Banner>}
                {event.state === "failed" && (
                  <Button
                    disabled={
                      locked ||
                      !allowed(event.kind) ||
                      event.connection_id !== data.connection?.id ||
                      event.domain !== data.connection?.domain
                    }
                    onClick={() => setReview(event)}
                  >
                    Review retry
                  </Button>
                )}
              </BlockStack>
            </Card>
          ))}
          {!data.events.length && data.counts.length > 0 && (
            <Text as="p">No updates match the selected state.</Text>
          )}
        </>
      )}
      <InlineStack gap="200">
        {!!pages.length && (
          <Button onClick={() => setPages((p) => p.slice(0, -1))}>
            Newer updates
          </Button>
        )}
        {data?.nextCursor && (
          <Button onClick={() => setPages((p) => [...p, data.nextCursor!])}>
            Older updates
          </Button>
        )}
      </InlineStack>
      <Modal
        open={!!review}
        title="Review source update retry"
        onClose={() => setReview(null)}
        primaryAction={{
          content: "Confirm retry",
          disabled: locked || !review || !allowed(review.kind),
          onAction: () => void send(),
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setReview(null) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">
              Store: {review?.domain}. {review?.kind} ID:{" "}
              {review?.resource_gid?.split("/").pop()}.
            </Text>
            <Text as="p">
              Continue from saved progress using current Shopify data. If the
              product changed, its variants are read again. Warehouse stock,
              costs and history stay unchanged.
            </Text>
            <Text as="p">
              This action can update customer contact details or remove a
              customer lookup if Shopify confirms its deletion. It does not send
              customer messages.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
}
